import { useEffect, useState } from 'react';
import { Container, Typography, makeStyles, Snackbar } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { string, object } from 'yup';
import { Edit } from '@material-ui/icons';

import SearchInput from '../tableElements/searchInput';
import Table from '../tableElements/table';
import ManagementModal from '../managementModal';
import Notification from 'src/components/ui/notification';

import { getUsers, editUser } from 'src/state/entities/users/actions';
import { getUsersData } from 'src/state/entities/users/selectors';
import { useCurrent } from 'src/helpers/hooks';
import { internationalPhone } from 'src/helpers/validation';

import { Column } from '../tableElements/types';
import { User } from './types';
import { getCompaniesData } from 'src/state/entities/companies/selectors';
import { getCompanies } from 'src/state/entities/companies/actions';

type UserWithCompany = User & {
  company: string | undefined;
};

const useStyles = makeStyles((theme) => ({
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    padding: '20px 0 0',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  container: {
    padding: '20px',
    width: '100%',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
}));

const columns: Column[] = [
  { id: 'userId', label: 'Id' },
  { id: 'name', label: 'Name' },
  {
    id: 'phoneNumber',
    label: 'Phone Number',
  },
  {
    id: 'emailAddress',
    label: 'Email',
  },
  {
    id: 'company',
    label: 'Company',
  },
  {
    action: true,
    id: 'actions',
    label: '',
  },
];

const validationSchema = object({
  name: string().min(0, 'Name is required.').required('Name is required.'),
  userPhoneNumber: internationalPhone({
    message: 'Enter country code followed by phone number: +1-999-999-9999',
  }).nullable(),
});

const UsersList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const usersData = useSelector(getUsersData);
  const companiesData = useSelector(getCompaniesData);
  const [searchValue, setSearchValue] = useState('');
  const [editData, setEditData] = useState<UserWithCompany | null>(null);
  const [showMessage, setShowMessage] = useState<boolean>(false);

  const action = useCurrent(getUsers.request(1, searchValue));
  const companiesAction = useCurrent(getCompanies.request(1, ''));

  useEffect(() => {
    if (action.current && !usersData.items.length) {
      dispatch(action.current);
    }

    if (companiesAction.current && !companiesData.items.length) {
      dispatch(companiesAction.current);
    }
  }, [action, companiesAction, dispatch, usersData.items.length, companiesData.items.length]);

  const data = usersData.items.map((user) => ({
    ...user,
    company: companiesData.items.find((company) => company.id === user?.customerId)?.name,
  }));

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    dispatch(getUsers.request(value, searchValue));
  };

  const handleSearch = (searchText: string) => {
    setSearchValue(searchText);
    dispatch(getUsers.request(1, searchText));
  };

  const handleEdit = (id: Number) => {
    const user = usersData.items.find((user) => user.userId === id);
    const company = companiesData.items.find((company) => company.id === user?.customerId);

    if (user) {
      setEditData({ ...user, company: company?.name });
    }
  };

  const handleClose = () => {
    setEditData(null);
  };

  const actions = [
    {
      Icon: Edit,
      action: handleEdit,
      title: 'Edit',
    },
  ];

  const onSubmit = (values: Record<string, string | number>) => {
    const userValues = values as unknown as UserWithCompany;
    const { company, ...userValuesWithoutCompany } = userValues;
    dispatch(editUser.request(userValues.userId, userValuesWithoutCompany));
  };

  useEffect(() => {
    if (!usersData.saving && !usersData.error) {
      setEditData(null);

      dispatch(getUsers.request(usersData.pageNumber, searchValue));
    }
  }, [usersData.saving, dispatch, searchValue, usersData.error, usersData.pageNumber]);

  useEffect(() => {
    if (usersData.error || usersData.success) {
      setShowMessage(true);
    }
  }, [usersData.error, usersData.success]);

  return (
    <Container className={classes.container}>
      <Container className={classes.header}>
        <Typography className={classes.title}>Users Management</Typography>
        <SearchInput placeholder="Search user..." handleSearch={handleSearch} />
      </Container>
      <Table
        actions={actions}
        columns={columns}
        data={data}
        emptyStateMessage="No users found."
        handleChangePage={handleChangePage}
        isLoading={usersData.loading}
        pageNumber={usersData.pageNumber}
        perPage={10}
        total={usersData.totalItems}
      />
      <ManagementModal
        fields={[
          { id: 'name', label: 'Name' },
          { id: 'phoneNumber', label: 'Phone Number' },
          { id: 'emailAddress', label: 'Email', disabled: true },
          { id: 'company', label: 'Company', disabled: true },
        ]}
        initialValues={
          (editData || { name: '', phoneNumber: '', emailAddress: '', company: '' }) as Record<
            string,
            string | number
          >
        }
        isOpen={!!editData}
        isSubmitting={!!usersData.saving}
        onClose={handleClose}
        onSubmit={onSubmit}
        title={'Edit User'}
        validationSchema={validationSchema}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={() => setShowMessage(false)}
        open={showMessage}
      >
        <Notification severity={usersData.error ? 'error' : 'success'}>
          {usersData.error
            ? typeof usersData.error === 'string'
              ? usersData.error
              : 'Something went wrong. Please try again.'
            : 'The user was saved.'}
        </Notification>
      </Snackbar>
    </Container>
  );
};

export default UsersList;
